import { useTranslation } from '../../../i18n/useTranslation';
import { Alert } from '../../atoms/Alert/Alert';
import type { IcpBelowDetection } from '../../../services/icpAnalysisService';

export interface IcpImportNoticeProps {
  /** Number of values stored from the report. */
  parameterCount: number;
  /** Parameters the report marks as below the detection limit (not stored). */
  belowDetection: ReadonlyArray<IcpBelowDetection>;
  onDismiss: () => void;
}

/**
 * Notice shown once after an ICP import: how many values were stored and
 * which parameters were left out because they were below the lab's detection
 * limit ("25 values imported · 18 parameters were below …: Nitrate, …").
 * Info tone, announced politely (CLAUDE.md §9h).
 */
export function IcpImportNotice({ parameterCount, belowDetection, onDismiss }: IcpImportNoticeProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language;

  const names = belowDetection.map((entry) => {
    // Translated name where the app has one, else the lab's own label, else the key.
    const name = t(`icp.parameterNames.${entry.key}`, { defaultValue: entry.providerParameterName ?? entry.key });
    if (entry.limit === undefined) return name;
    return t('icp.importNotice.withLimit', {
      name,
      limit: entry.limit.toLocaleString(locale, { maximumFractionDigits: 6 }),
      unit: entry.unit ?? '',
    }).replace(/\s+\)$/, ')');
  });

  return (
    <Alert
      tone="info"
      title={t('icp.importNotice.imported', { count: parameterCount })}
      onDismiss={onDismiss}
      dismissLabel={t('icp.importNotice.dismiss')}
      data-testid="icp-import-notice"
    >
      {names.length > 0
        ? t('icp.importNotice.belowDetection', {
            count: names.length,
            names: names.join(t('icp.importNotice.listSeparator')),
          })
        : null}
    </Alert>
  );
}

export default IcpImportNotice;
